import { DateTime } from "luxon";
import type { FactureDuplicateCandidate } from "compta-shared";
import ConfirmModal from "./ConfirmModal.tsx";
import { Link } from "wouter";

type DuplicateFactureWarningModalItem = {
  docId?: string;
  title: string;
  duplicates: FactureDuplicateCandidate[];
};

interface DuplicateFactureWarningModalProps {
  show: boolean;
  items: DuplicateFactureWarningModalItem[];
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  isSubmitting?: boolean;
  blocking?: boolean;
  description?: string;
}

const formatDate = (date: Date | null) => {
  if (!date) {
    return "Date inconnue";
  }

  return DateTime.fromJSDate(date).toFormat("dd/MM/yyyy");
};

const formatAmount = (montantCent: number) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(Math.abs(montantCent) / 100);
};

export const formatMatchedOn = (matchedOn: FactureDuplicateCandidate["matchedOn"]) => {
  return matchedOn
    .map((match) =>
      match === "modeRef" ? "même référence" : "même date et montant",
    )
    .join(" + ");
};

export const getDuplicateConfirmationKeys = (duplicates: FactureDuplicateCandidate[]) =>
  duplicates.map((candidate) => `${candidate.kind}:${candidate._id}`);

const DuplicateFactureWarningModal = ({
  show,
  items,
  onCancel,
  onConfirm,
  confirmLabel = "Créer quand même",
  isSubmitting = false,
  blocking = false,
  description,
}: DuplicateFactureWarningModalProps) => {
  return (
    <ConfirmModal
      show={show}
      title={blocking ? "Doublon détecté" : "Doublon possible détecté"}
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmLabel={confirmLabel}
      cancelLabel={blocking ? "Fermer" : undefined}
      submittingLabel="Enregistrement..."
      confirmVariant="warning"
      isSubmitting={isSubmitting}
      hideConfirm={blocking}
    >
        <p className="mb-3">
          {description ||
            (blocking
              ? "Cette facture ne peut pas être ajoutée aux règlements en attente tant que ce doublon existe."
              : "Une ou plusieurs factures existantes semblent correspondre. Vérifie la liste ci-dessous avant de continuer.")}
        </p>
        <div className="d-flex flex-column gap-3">
          {items.map((item) => (
            <div key={item.title} className="border rounded p-3">
              <div className="fw-semibold mb-2">{item.title}</div>
              <div className="d-flex flex-column gap-2">
                {item.duplicates.map((duplicate) => (
                  <div
                    key={`${item.title}-${duplicate._id}`}
                    className="bg-body-tertiary rounded p-2"
                  >
                    <div>
                      <strong>{duplicate.libellé}</strong>
                      <span className="badge text-bg-secondary ms-2">
                        {duplicate.kind === "facture" ? "Facture comptabilisée" : duplicate.kind === "draft" ? "Facture en attente de règlement" : "Même lot"}
                      </span>
                    </div>
                    <div className="small text-body-secondary">
                      Réf. {duplicate.modeRef || "-"} • {formatDate(duplicate.date)}
                      {" • "}
                      {formatAmount(duplicate.montantCent)}
                    </div>
                    <div className="small text-warning-emphasis">
                      Match: {formatMatchedOn(duplicate.matchedOn)}
                    </div>
                    {duplicate.kind !== "batch" ? (
                      <Link className="btn btn-outline-primary btn-sm mt-2"
                        href={duplicate.kind === "facture"
                          ? `~/factures/factureInput?factureId=${encodeURIComponent(duplicate._id)}`
                          : `~/factures/facturesAPayer?draftId=${encodeURIComponent(duplicate._id)}`}
                        onClick={onCancel}>
                        {duplicate.kind === "facture" ? "Voir la facture existante" : "Voir la facture à régler"}
                      </Link>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            ))}
          </div>
      </ConfirmModal>
  );
};

export type { DuplicateFactureWarningModalItem };
export default DuplicateFactureWarningModal;
